import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getBrowserTimezone,
  getLocaleForLanguage,
  formatDateInTimezone,
  formatDateTimeInTimezone,
} from '../utils/timezone';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'pt');

  // Lista de idiomas suportados
  const supportedLanguages = useMemo(() => [
    { code: 'pt', name: 'Português (Brasil)', flag: '🇧🇷' },
    { code: 'en', name: 'English (US)', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ], []);

  // Função para alterar o idioma
  const changeLanguage = useCallback(async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
      
      // Salvar no localStorage
      localStorage.setItem('i18nextLng', languageCode);
      
      // Opcional: Configurar locale para formatação de datas
      if (languageCode === 'pt') {
        document.documentElement.lang = 'pt-BR';
      } else if (languageCode === 'en') {
        document.documentElement.lang = 'en-US';
      } else if (languageCode === 'es') {
        document.documentElement.lang = 'es-ES';
      }
    } catch (error) {
      console.error('Erro ao alterar idioma:', error);
    }
  }, [i18n]);

  // Função para obter informações do idioma atual
  const getCurrentLanguageInfo = () => {
    return supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];
  };

  const timezone = useMemo(() => getBrowserTimezone(), []);

  const formatDate = useCallback((date, options = {}) => {
    return formatDateInTimezone(date, {
      locale: getLocaleForLanguage(currentLanguage),
      timezone,
      options,
    });
  }, [currentLanguage, timezone]);

  const formatDateTime = useCallback((date, options = {}) => {
    const { emptyValue, ...formatOptions } = options;
    return formatDateTimeInTimezone(date, {
      locale: getLocaleForLanguage(currentLanguage),
      timezone,
      options: formatOptions,
      emptyValue,
    });
  }, [currentLanguage, timezone]);

  // Função para formatar números de acordo com o idioma atual
  const formatNumber = (number, options = {}) => {
    const locales = {
      pt: 'pt-BR',
      en: 'en-US',
      es: 'es-ES'
    };

    return new Intl.NumberFormat(locales[currentLanguage] || 'pt-BR', options).format(number);
  };

  // Sincronizar o estado quando o i18n muda o idioma
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Configurar o idioma inicial
  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng');
    if (savedLanguage && supportedLanguages.some(lang => lang.code === savedLanguage)) {
      changeLanguage(savedLanguage);
    }
  }, [changeLanguage, supportedLanguages]);

  const value = {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    getCurrentLanguageInfo,
    timezone,
    formatDate,
    formatDateTime,
    formatNumber,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext; 